import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Sparkles, Upload, RefreshCcw, AlertTriangle, Image as ImageIcon,
  Trash2, Plus, ExternalLink, Search, CheckSquare, Square,
  ChevronDown, ChevronUp, Loader2, CheckCircle2, GripVertical,
  TrendingUp, ArrowUpDown, Check, X, Pencil, Clock, Link as LinkIcon,
  Layers, RotateCcw,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import { api, ApiError, type ProductImage, type CuratedProduct, type CuratedState, type CompetitorPrice, type PricingProposal, type TdsynnexProduct, type StoreProduct } from "@/lib/api";
import { useShopifyAdminURL } from "@/lib/useShopifyAdmin";
import { formatUsd } from "@/lib/utils";
import { toast } from "sonner";
import { PricingSlider } from "./PricingSlider";
import { PricingIntelligence } from "./PricingIntelligence";

// ── helpers ──────────────────────────────────────────────────────────────────

function charCount(val: string | null | undefined, max: number) {
  const n = (val ?? "").length;
  return (
    <span className={n > max ? "text-rose-500" : n > max * 0.9 ? "text-amber-500" : "text-muted-foreground"}>
      {n}/{max}
    </span>
  );
}

function seoPreview(title: string | null, description: string | null, url = "store.oncloudtech.io") {
  return (
    <div className="rounded-lg border border-border bg-card p-4 font-sans text-sm space-y-1">
      <div className="text-xs text-muted-foreground">{url}</div>
      <div className="text-blue-500 text-base font-medium leading-snug truncate">
        {title || <span className="italic text-muted-foreground">No SEO title</span>}
      </div>
      <div className="text-muted-foreground leading-snug line-clamp-2">
        {description || <span className="italic">No SEO description</span>}
      </div>
    </div>
  );
}

// ── types ─────────────────────────────────────────────────────────────────────

interface AgentStep { text: string }
interface SearchState {
  status: "idle" | "running" | "done" | "error";
  steps: AgentStep[];
  candidates: string[];   // found URLs not yet saved
  selected: Set<string>;  // URLs checked by user
  logOpen: boolean;
}

// ── ImageGallery ─────────────────────────────────────────────────────────────

function ImageGallery({ curatedId, shopifyProductId, mpn, brand, description, title }: {
  curatedId: string; shopifyProductId?: string | null; mpn: string; brand: string; description?: string; title?: string;
}) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [urlInput, setUrlInput] = useState("");
  const [altInput, setAltInput] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [uploadQueue, setUploadQueue] = useState<{ file: File; status: "pending" | "done" | "error" }[]>([]);
  const [pageUrl, setPageUrl] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [extractedCandidates, setExtractedCandidates] = useState<string[]>([]);
  const [extractedSelected, setExtractedSelected] = useState<Set<string>>(new Set());
  const [savingExtracted, setSavingExtracted] = useState(false);

  const [search, setSearch] = useState<SearchState>({
    status: "idle", steps: [], candidates: [], selected: new Set(), logOpen: true,
  });
  const [savingUrls, setSavingUrls] = useState(false);

  const images = useQuery({
    queryKey: ["images", curatedId],
    queryFn: () => api.listImages(curatedId),
    enabled: !!curatedId,
    staleTime: 30_000,
  });

  const [brokenUrls, setBrokenUrls] = useState<Set<string>>(new Set());

  const deleteMut = useMutation({
    mutationFn: (imageId: string) => api.deleteImage(curatedId, imageId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["images", curatedId] }),
  });

  const pullMut = useMutation({
    mutationFn: () => api.storePull(curatedId),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["images", curatedId] });
      setBrokenUrls(new Set());
      const change = data.changes?.[0] ?? "synced";
      toast.success(`Images synced — ${change}`);
    },
    onError: () => toast.error("Image sync failed — check Shopify connection"),
  });

  const reorderMut = useMutation({
    mutationFn: (ids: string[]) => api.reorderImages(curatedId, ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["images", curatedId] }),
  });

  function moveImage(fromIdx: number, toIdx: number) {
    const current = images.data?.items ?? [];
    if (toIdx < 0 || toIdx >= current.length) return;
    const reordered = [...current];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    reorderMut.mutate(reordered.map(img => img.id));
  }

  async function uploadFiles(files: File[]) {
    const queue = files.map(f => ({ file: f, status: "pending" as const }));
    setUploadQueue(queue);
    for (let i = 0; i < files.length; i++) {
      try {
        await api.uploadImageFile(curatedId, files[i], altInput || undefined);
        setUploadQueue(q => q.map((e, idx) => idx === i ? { ...e, status: "done" } : e));
      } catch {
        setUploadQueue(q => q.map((e, idx) => idx === i ? { ...e, status: "error" } : e));
      }
    }
    await qc.invalidateQueries({ queryKey: ["images", curatedId] });
    setTimeout(() => setUploadQueue([]), 2000);
  }

  const uploadUrlMut = useMutation({
    mutationFn: () => api.uploadImageUrl(curatedId, urlInput.trim(), altInput || undefined, "manual"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["images", curatedId] });
      setUrlInput("");
      setAltInput("");
    },
  });

  const runSearch = useCallback(() => {
    if (!mpn && !brand) return;
    setSearch({ status: "running", steps: [], candidates: [], selected: new Set(), logOpen: true });

    const params = new URLSearchParams({ mpn, brand });
    if (description) params.set("description", description.slice(0, 200));
    if (title) params.set("title", title.slice(0, 120));
    const es = new EventSource(`/api/images/search?${params}`);

    es.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data) as { type: string; text?: string; urls?: string[]; count?: number; message?: string };
        if (msg.type === "step") {
          setSearch(s => ({ ...s, steps: [...s.steps, { text: msg.text ?? "" }] }));
        } else if (msg.type === "found") {
          const urls = msg.urls ?? [];
          setSearch(s => ({ ...s, candidates: urls, selected: new Set(urls) }));
        } else if (msg.type === "done") {
          const finalUrls = msg.urls ?? [];
          setSearch(s => ({
            ...s,
            status: "done",
            candidates: finalUrls.length ? finalUrls : s.candidates,
            selected: finalUrls.length ? new Set(finalUrls) : s.selected,
            steps: [
              ...s.steps,
              { text: msg.message ?? (finalUrls.length ? `Found ${finalUrls.length} image(s).` : "No images found.") },
            ],
          }));
          es.close();
        }
      } catch { /* ignore parse errors */ }
    };

    es.onerror = () => {
      setSearch(s => ({
        ...s,
        status: "error",
        steps: [...s.steps, { text: "Connection error — is agents-py running?" }],
      }));
      es.close();
    };
  }, [mpn, brand]);

  async function saveSelected() {
    setSavingUrls(true);
    const alt = `${brand} ${mpn}`.trim();
    for (const url of search.selected) {
      try {
        await api.uploadImageUrl(curatedId, url, alt, "manual");
      } catch { /* continue on error */ }
    }
    await qc.invalidateQueries({ queryKey: ["images", curatedId] });
    setSearch(s => ({ ...s, candidates: [], selected: new Set(), status: "idle" }));
    setSavingUrls(false);
  }

  function toggleCandidate(url: string) {
    setSearch(s => {
      const next = new Set(s.selected);
      next.has(url) ? next.delete(url) : next.add(url);
      return { ...s, selected: next };
    });
  }

  async function runExtractFromUrl() {
    if (!pageUrl.trim()) return;
    setExtracting(true);
    setExtractedCandidates([]);
    setExtractedSelected(new Set());
    try {
      const res = await fetch("/api/images/from-url", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: pageUrl.trim(), curated_id: curatedId }),
      });
      const data = await res.json() as { images: { url: string }[]; message: string };
      const urls = data.images?.map((i: { url: string }) => i.url) ?? [];
      setExtractedCandidates(urls);
      setExtractedSelected(new Set(urls));
      if (urls.length) {
        await qc.invalidateQueries({ queryKey: ["images", curatedId] });
        toast.success(data.message ?? `${urls.length} image(s) extracted`);
      } else {
        toast.warning(data.message ?? "No images found at that URL");
      }
    } catch {
      toast.error("Extraction failed — check agents-py");
    } finally {
      setExtracting(false);
    }
  }

  async function saveExtracted() {
    setSavingExtracted(true);
    const alt = `${brand} ${mpn}`.trim();
    for (const url of extractedSelected) {
      try { await api.uploadImageUrl(curatedId, url, alt, "manual"); } catch { /* continue */ }
    }
    await qc.invalidateQueries({ queryKey: ["images", curatedId] });
    setExtractedCandidates([]);
    setExtractedSelected(new Set());
    setSavingExtracted(false);
  }

  const items: ProductImage[] = images.data?.items ?? [];

  return (
    <div className="space-y-5">
      {/* ── Pull from Shopify ── */}
      {shopifyProductId && (
        <div className="flex items-center justify-between rounded-md border border-border bg-muted/20 px-3 py-2">
          <span className="text-xs text-muted-foreground">
            Shopify images are pulled into this gallery. Re-sync to refresh.
          </span>
          <Button size="sm" variant="outline" onClick={() => pullMut.mutate()} disabled={pullMut.isPending}>
            {pullMut.isPending
              ? <><Loader2 className="h-3 w-3 animate-spin mr-1" />Pulling…</>
              : <><RefreshCcw className="h-3 w-3 mr-1" />Sync from Shopify</>}
          </Button>
        </div>
      )}

      {/* ── Saved gallery ── */}
      {items.length > 0 ? (
        <div className="grid grid-cols-3 gap-3">
          {items.map((img, i) => (
            <div key={img.id} className="relative group rounded-lg border border-border overflow-hidden bg-muted aspect-square">
              {brokenUrls.has(img.url) ? (
                <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-muted-foreground p-2">
                  <ImageIcon className="h-6 w-6 opacity-30" />
                  <span className="text-[10px] text-center break-all opacity-50 leading-tight">{img.source}</span>
                  <a href={img.url} target="_blank" rel="noreferrer" className="text-[10px] text-blue-400 hover:underline">Open URL</a>
                </div>
              ) : (
                <img
                  src={img.url}
                  alt={img.alt ?? ""}
                  className="w-full h-full object-contain p-2"
                  loading="lazy"
                  onError={() => setBrokenUrls(s => new Set(s).add(img.url))}
                />
              )}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <a href={img.url} target="_blank" rel="noreferrer" className="p-1.5 rounded bg-white/20 hover:bg-white/40 text-white">
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
                {i > 0 && (
                  <button onClick={() => moveImage(i, i - 1)} className="p-1.5 rounded bg-white/20 hover:bg-white/40 text-white" title="Move up">
                    <GripVertical className="h-3.5 w-3.5" />
                  </button>
                )}
                <button
                  onClick={() => deleteMut.mutate(img.id)}
                  className="p-1.5 rounded bg-rose-500/80 hover:bg-rose-500 text-white"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="absolute bottom-1 left-1">
                <Badge variant="outline" className="text-[10px] py-0 px-1 bg-black/60 text-white border-white/20">
                  {i === 0 ? "Cover" : `#${i + 1}`}
                </Badge>
              </div>
              {img.source && img.source !== "manual" && (
                <div className="absolute top-1 right-1">
                  <Badge variant="outline" className="text-[10px] py-0 px-1 bg-black/60 text-emerald-400 border-emerald-400/30 capitalize">
                    {img.source}
                  </Badge>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : search.status === "idle" ? (
        <div className="flex flex-col items-center justify-center gap-3 py-12 rounded-lg border-2 border-dashed border-border text-muted-foreground">
          <ImageIcon className="h-8 w-8 opacity-40" />
          <span className="text-sm">No images yet — search vendor or add manually</span>
        </div>
      ) : null}

      {/* ── Agent search panel ── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <Search className="h-4 w-4 text-emerald-500" />
              Search vendor for images
            </CardTitle>
            {search.steps.length > 0 && (
              <button
                onClick={() => setSearch(s => ({ ...s, logOpen: !s.logOpen }))}
                className="text-muted-foreground hover:text-foreground"
              >
                {search.logOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              </button>
            )}
          </div>
          <CardDescription className="text-xs">
            Searches UPC database → {brand || "vendor"} website → Claude web search
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button
            size="sm"
            variant="outline"
            onClick={runSearch}
            disabled={search.status === "running" || (!mpn && !brand)}
            className="w-full"
          >
            {search.status === "running"
              ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />Searching…</>
              : <><Search className="h-3.5 w-3.5 mr-2" />Search for images</>}
          </Button>

          {/* Activity log */}
          {search.logOpen && search.steps.length > 0 && (
            <div className="rounded-md bg-muted border border-border p-3 space-y-1 max-h-36 overflow-y-auto">
              {search.steps.map((s, i) => (
                <div key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                  {i === search.steps.length - 1 && search.status === "running"
                    ? <Loader2 className="h-3 w-3 mt-0.5 shrink-0 animate-spin text-emerald-500" />
                    : <CheckCircle2 className="h-3 w-3 mt-0.5 shrink-0 text-emerald-500" />}
                  <span>{s.text}</span>
                </div>
              ))}
            </div>
          )}

          {/* Candidate preview grid */}
          {search.candidates.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{search.selected.size} of {search.candidates.length} selected</span>
                <button
                  className="text-emerald-500 hover:underline"
                  onClick={() => setSearch(s => ({
                    ...s,
                    selected: s.selected.size === s.candidates.length
                      ? new Set()
                      : new Set(s.candidates),
                  }))}
                >
                  {search.selected.size === search.candidates.length ? "Deselect all" : "Select all"}
                </button>
              </div>

              <div className="grid grid-cols-3 gap-2">
                {search.candidates.map((url) => {
                  const checked = search.selected.has(url);
                  return (
                    <button
                      key={url}
                      onClick={() => toggleCandidate(url)}
                      className={`relative rounded-lg border-2 overflow-hidden aspect-square transition-colors ${
                        checked ? "border-emerald-500" : "border-border opacity-50"
                      }`}
                    >
                      <img
                        src={url}
                        alt=""
                        className="w-full h-full object-contain p-1.5 bg-muted"
                        loading="lazy"
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.opacity = "0.3"; }}
                      />
                      <div className="absolute top-1 right-1">
                        {checked
                          ? <CheckSquare className="h-4 w-4 text-emerald-500 drop-shadow" />
                          : <Square className="h-4 w-4 text-muted-foreground" />}
                      </div>
                    </button>
                  );
                })}
              </div>

              <Button
                size="sm"
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={search.selected.size === 0 || savingUrls}
                onClick={saveSelected}
              >
                {savingUrls
                  ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />Saving…</>
                  : `Save ${search.selected.size} image${search.selected.size !== 1 ? "s" : ""} to gallery`}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Extract from product page URL ── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <LinkIcon className="h-4 w-4 text-sky-400" />
            Extract from product page
          </CardTitle>
          <CardDescription className="text-xs">
            Paste any manufacturer or retailer product page URL — Samsung, Dell, CDW, B&H, etc.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              placeholder="https://www.samsung.com/us/smartphones/…"
              value={pageUrl}
              onChange={(e) => setPageUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !extracting && runExtractFromUrl()}
              className="text-xs font-mono"
            />
            <Button size="sm" variant="outline" onClick={runExtractFromUrl} disabled={extracting || !pageUrl.trim()}>
              {extracting
                ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />Extracting…</>
                : "Extract"}
            </Button>
          </div>
          {extractedCandidates.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{extractedSelected.size} of {extractedCandidates.length} selected</span>
                <button className="text-sky-400 hover:underline" onClick={() =>
                  setExtractedSelected(s => s.size === extractedCandidates.length ? new Set() : new Set(extractedCandidates))
                }>
                  {extractedSelected.size === extractedCandidates.length ? "Deselect all" : "Select all"}
                </button>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {extractedCandidates.map((url) => {
                  const checked = extractedSelected.has(url);
                  return (
                    <button key={url} onClick={() => setExtractedSelected(s => { const n = new Set(s); n.has(url) ? n.delete(url) : n.add(url); return n; })}
                      className={`relative rounded-lg border-2 overflow-hidden aspect-square transition-colors ${checked ? "border-sky-400" : "border-border opacity-50"}`}>
                      <img src={url} alt="" className="w-full h-full object-contain p-1 bg-muted" loading="lazy"
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.opacity = "0.2"; }} />
                      <div className="absolute top-1 right-1">
                        {checked ? <CheckSquare className="h-3.5 w-3.5 text-sky-400 drop-shadow" /> : <Square className="h-3.5 w-3.5 text-muted-foreground" />}
                      </div>
                    </button>
                  );
                })}
              </div>
              <Button size="sm" className="w-full bg-sky-600 hover:bg-sky-700 text-white" disabled={extractedSelected.size === 0 || savingExtracted} onClick={saveExtracted}>
                {savingExtracted
                  ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />Saving…</>
                  : `Save ${extractedSelected.size} image${extractedSelected.size !== 1 ? "s" : ""} to gallery`}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Manual add ── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Add image manually
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Drag-and-drop zone */}
          <div
            className={`relative rounded-lg border-2 border-dashed transition-colors cursor-pointer ${
              dragOver ? "border-emerald-500 bg-emerald-500/5" : "border-border hover:border-muted-foreground/50"
            }`}
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith("image/"));
              if (files.length) uploadFiles(files);
            }}
          >
            <div className="flex flex-col items-center justify-center gap-2 py-8 text-muted-foreground select-none">
              <Upload className="h-6 w-6 opacity-50" />
              <span className="text-sm">Drop images here or <span className="text-emerald-500">browse</span></span>
              <span className="text-xs opacity-60">Multiple files supported · JPG, PNG, WebP</span>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                if (files.length) uploadFiles(files);
                e.target.value = "";
              }}
            />
          </div>

          {/* Upload progress */}
          {uploadQueue.length > 0 && (
            <div className="space-y-1">
              {uploadQueue.map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  {item.status === "pending"
                    ? <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                    : item.status === "done"
                    ? <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                    : <AlertTriangle className="h-3 w-3 text-rose-500" />}
                  <span className="truncate text-muted-foreground">{item.file.name}</span>
                </div>
              ))}
            </div>
          )}

          {/* URL import */}
          <div className="flex gap-2">
            <Input
              placeholder="Or paste an image URL…"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && urlInput.trim() && uploadUrlMut.mutate()}
              className="text-sm font-mono"
            />
            <Button
              size="sm"
              variant="outline"
              disabled={!urlInput.trim() || uploadUrlMut.isPending}
              onClick={() => uploadUrlMut.mutate()}
              className="shrink-0"
            >
              {uploadUrlMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Add"}
            </Button>
          </div>

          {/* URL preview */}
          {urlInput.trim() && (
            <div className="rounded-md border border-border overflow-hidden aspect-video bg-muted flex items-center justify-center">
              <img
                src={urlInput.trim()}
                alt="preview"
                className="max-h-full max-w-full object-contain"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.opacity = "0.15"; }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-[11px] text-muted-foreground leading-relaxed">
        Cover image (position 0) is the Shopify featured image. Hover saved images to delete.
      </p>
    </div>
  );
}

// ── ShopifyPreview ───────────────────────────────────────────────────────────

function DiffField({
  label,
  value,
  state,
  hint,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  state: "synced" | "drifted" | "will-update" | "unknown";
  hint?: string;
  mono?: boolean;
}) {
  const colors = {
    synced:      "border-emerald-500/30 bg-emerald-500/5",
    drifted:     "border-amber-500/40 bg-amber-500/10",
    "will-update": "border-amber-400/20 bg-amber-400/5",
    unknown:     "border-border bg-muted/20",
  };
  const badges = {
    synced:      <span className="text-[10px] text-emerald-500 font-medium">in sync</span>,
    drifted:     <span className="text-[10px] text-amber-500 font-medium">will change ↑</span>,
    "will-update": <span className="text-[10px] text-amber-400/80">will sync</span>,
    unknown:     null,
  };
  return (
    <div className={`rounded-md border px-3 py-2 ${colors[state]}`}>
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
        <span className="flex items-center gap-2">
          {hint && <span className="text-[10px] text-muted-foreground">{hint}</span>}
          {badges[state]}
        </span>
      </div>
      <div className={`text-sm ${mono ? "font-mono text-xs" : ""}`}>{value}</div>
    </div>
  );
}

function CollectionsSelector({ curatedId, suggestedIds }: {
  curatedId: string; shopifyProductId?: string | null; suggestedIds?: number[];
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data: allData } = useQuery({
    queryKey: ["shopify-collections"],
    queryFn: () => api.listCollections(),
    staleTime: 5 * 60_000,
  });

  const { data: myData, refetch } = useQuery({
    queryKey: ["curated-collections", curatedId],
    queryFn: () => api.getCuratedCollections(curatedId),
    staleTime: 30_000,
  });

  const all = allData?.items ?? [];
  const mine = new Set((myData?.items ?? []).map((c) => c.id));
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // Sync local selection when data loads; fall back to AI suggestions when no DB memberships exist
  useEffect(() => {
    if (mine.size > 0) {
      setSelected(new Set(mine));
    } else if (suggestedIds && suggestedIds.length > 0) {
      setSelected(new Set(suggestedIds));
    } else {
      setSelected(new Set());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myData, suggestedIds?.join(",")]);

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.updateCuratedCollections(curatedId, Array.from(selected));
      await refetch();
      qc.invalidateQueries({ queryKey: ["curated-collections", curatedId] });
      setOpen(false);
      toast.success("Collections updated");
    } catch {
      toast.error("Failed to update collections");
    } finally {
      setSaving(false);
    }
  };

  const syncCollections = async () => {
    try {
      const r = await api.syncCollections();
      qc.invalidateQueries({ queryKey: ["shopify-collections"] });
      toast.success(`Synced ${r.synced} collections from Shopify`);
    } catch {
      toast.error("Failed to sync collections");
    }
  };

  const currentTitles = (myData?.items ?? []).map((c) => c.title);

  return (
    <div className="rounded-md border border-border bg-muted/20 px-3 py-2">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <Layers className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Collections</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={syncCollections} title="Sync collections from Shopify"
            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent transition-colors">
            <RotateCcw className="h-3 w-3" />
          </button>
          <button onClick={() => setOpen((o) => !o)}
            className="text-[10px] text-muted-foreground hover:text-foreground transition-colors px-1">
            {open ? "Cancel" : "Edit"}
          </button>
        </div>
      </div>

      {!open && (
        <div className="flex flex-wrap gap-1">
          {currentTitles.length > 0
            ? currentTitles.map((t) => <Badge key={t} variant="secondary" className="text-xs font-normal">{t}</Badge>)
            : suggestedIds && suggestedIds.length > 0 && all.length > 0
              ? <>
                  {all.filter(c => suggestedIds.includes(c.id)).map(c =>
                    <Badge key={c.id} variant="outline" className="text-xs font-normal border-amber-500/50 text-amber-600 dark:text-amber-400">
                      {c.title} ✦
                    </Badge>
                  )}
                  <span className="text-[10px] text-muted-foreground italic self-center">AI suggested — click Edit to confirm</span>
                </>
              : <span className="text-xs text-muted-foreground italic">No collections</span>
          }
        </div>
      )}

      {open && (
        <div className="space-y-1 mt-1">
          {all.length === 0 && (
            <p className="text-xs text-muted-foreground">No collections found — click the refresh icon to sync from Shopify.</p>
          )}
          <div className="max-h-48 overflow-y-auto space-y-0.5">
            {all.map((col) => (
              <label key={col.id} className="flex items-center gap-2 px-1 py-0.5 rounded hover:bg-accent cursor-pointer">
                <input type="checkbox" checked={selected.has(col.id)} onChange={() => toggle(col.id)}
                  className="h-3.5 w-3.5 rounded border-border accent-primary" />
                <span className="text-sm">{col.title}</span>
                <span className="text-xs text-muted-foreground ml-auto">/{col.handle}</span>
              </label>
            ))}
          </div>
          <div className="flex justify-end pt-1">
            <Button size="sm" onClick={save} disabled={saving} className="h-7 text-xs">
              {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Check className="h-3 w-3 mr-1" />}
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ShopifyPreview({ c, p, storeItem }: {
  c: CuratedProduct;
  p: TdsynnexProduct;
  storeItem: StoreProduct | null;
}) {
  const shopifyAdminBase = useShopifyAdminURL();
  const vendor = c.vendor || p.brand_slug?.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase()) || p.manufacturer_name;
  const price = c.proposed_price ?? p.contract_price;
  const compareAt = c.compare_at_price ?? p.msrp;
  const effectiveQty = c.qty_override ?? p.qty_on_hand ?? 0;
  const targetStatus = effectiveQty === 0 ? "DRAFT" : (c.status === "published" ? "ACTIVE" : "DRAFT");

  const hasShopify = !!storeItem?.shopify_status;

  // Field-level diff helpers
  const fieldState = (local: string | null | undefined, shopify: string | null | undefined): "synced" | "drifted" | "unknown" => {
    if (!hasShopify) return "unknown";
    const l = (local ?? "").trim();
    const s = (shopify ?? "").trim();
    return l === s ? "synced" : "drifted";
  };
  const tagsState = (): "synced" | "drifted" | "unknown" => {
    if (!hasShopify) return "unknown";
    const local = [...(c.tags ?? [])].sort().join(",");
    const shopify = [...(storeItem?.shopify_tags ?? [])].sort().join(",");
    return local === shopify ? "synced" : "drifted";
  };
  const priceState = (): "synced" | "drifted" | "unknown" => {
    if (!hasShopify || !storeItem?.shopify_price) return "unknown";
    const localF = parseFloat(price ?? "0");
    const shopF = parseFloat(storeItem.shopify_price);
    return Math.abs(localF - shopF) <= 0.01 ? "synced" : "drifted";
  };
  const statusMatch = !hasShopify || storeItem?.shopify_status === targetStatus;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Live diff vs Shopify — {hasShopify ? "comparing against current store state." : "publish first to enable comparison."}
        </p>
        <div className="flex items-center gap-3 text-[10px]">
          <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-emerald-500/40 border border-emerald-500/50" />in sync</span>
          <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-amber-500/40 border border-amber-500/50" />will change</span>
        </div>
      </div>

      {/* Status */}
      <DiffField
        label="Shopify status"
        state={hasShopify ? (statusMatch ? "synced" : "drifted") : "unknown"}
        value={
          <div className="flex items-center gap-2">
            <Badge variant={targetStatus === "ACTIVE" ? "success" : "outline"} className="text-xs">{targetStatus}</Badge>
            {!statusMatch && storeItem?.shopify_status && (
              <span className="text-xs text-muted-foreground">currently: <Badge variant="outline" className="text-xs">{storeItem.shopify_status}</Badge></span>
            )}
          </div>
        }
      />

      {/* Price */}
      <DiffField
        label="Price"
        state={priceState()}
        hint={priceState() === "drifted" && storeItem?.shopify_price ? `Shopify: $${Number(storeItem.shopify_price).toFixed(2)}` : undefined}
        value={
          <div className="flex items-baseline gap-2">
            <span className="font-semibold tabular-nums">
              {price ? `$${Number(price).toFixed(2)}` : <span className="text-muted-foreground italic">No price set</span>}
            </span>
            {compareAt && Number(compareAt) > Number(price ?? 0) && (
              <span className="text-muted-foreground line-through tabular-nums text-xs">${Number(compareAt).toFixed(2)}</span>
            )}
            <span className="text-xs text-muted-foreground tabular-nums">
              ({effectiveQty} in stock{c.qty_override != null ? " · manual" : ""})
            </span>
          </div>
        }
      />

      {/* Inventory (Shopify live) */}
      <div className="grid grid-cols-2 gap-3">
        <DiffField
          label="Shopify inventory"
          state={hasShopify ? "synced" : "unknown"}
          hint={hasShopify ? "live from Shopify" : undefined}
          value={
            hasShopify
              ? <span className="tabular-nums font-medium">
                  {storeItem?.shopify_inventory_qty ?? 0} units
                  {storeItem?.shopify_inventory_qty !== effectiveQty && (
                    <span className="ml-2 text-[10px] text-amber-500">local: {effectiveQty}</span>
                  )}
                </span>
              : <span className="text-muted-foreground italic">not published</span>
          }
        />
        <DiffField
          label="TD SYNNEX stock"
          state="unknown"
          value={<span className="tabular-nums font-medium">{effectiveQty} units{c.qty_override != null ? " (manual)" : ""}</span>}
        />
      </div>

      {/* Weight / dimensions */}
      {(storeItem?.shopify_weight || p.ship_weight) && (
        <div className="rounded-md border border-border/40 bg-muted/20 px-3 py-2 space-y-1.5">
          <p className="text-[10px] font-medium tracking-widest uppercase text-muted-foreground/50">Shipping</p>
          <div className="flex flex-wrap gap-4 text-xs">
            {storeItem?.shopify_weight && (
              <span>
                <span className="text-muted-foreground">Shopify weight </span>
                <span className="font-medium tabular-nums">
                  {storeItem.shopify_weight} {storeItem.shopify_weight_unit?.toLowerCase() ?? "lb"}
                </span>
              </span>
            )}
            {p.ship_weight && (
              <span>
                <span className="text-muted-foreground">TD SYNNEX weight </span>
                <span className="font-medium tabular-nums">{p.ship_weight} lb</span>
              </span>
            )}
            {(p.length || p.width || p.height) && (
              <span>
                <span className="text-muted-foreground">Dims </span>
                <span className="font-medium tabular-nums">
                  {[p.length, p.width, p.height].filter(Boolean).join(" × ")} in
                </span>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Title */}
      <DiffField
        label="Title"
        state={fieldState(c.title, storeItem?.shopify_title)}
        hint={fieldState(c.title, storeItem?.shopify_title) === "drifted" ? `Shopify: ${storeItem?.shopify_title}` : undefined}
        value={c.title || <span className="text-muted-foreground italic">No title</span>}
      />

      {/* Vendor + Product type */}
      <div className="grid grid-cols-2 gap-3">
        <DiffField
          label="Vendor"
          state={fieldState(vendor, storeItem?.shopify_vendor)}
          hint={fieldState(vendor, storeItem?.shopify_vendor) === "drifted" ? `Shopify: ${storeItem?.shopify_vendor}` : undefined}
          value={vendor || <span className="text-muted-foreground italic">—</span>}
        />
        <DiffField
          label="Product type"
          state={fieldState(c.product_type, storeItem?.shopify_product_type)}
          hint={fieldState(c.product_type, storeItem?.shopify_product_type) === "drifted" ? `Shopify: ${storeItem?.shopify_product_type}` : undefined}
          value={c.product_type || <span className="text-muted-foreground italic">—</span>}
        />
      </div>

      {/* TD SYNNEX reference description */}
      {p.short_description && (
        <div className="rounded-md border border-border/40 bg-muted/20 px-3 py-2 space-y-1">
          <p className="text-[10px] font-medium tracking-widest uppercase text-muted-foreground/50">TD SYNNEX source description</p>
          <p className="text-xs text-muted-foreground leading-relaxed">{p.short_description}</p>
        </div>
      )}

      {/* Description */}
      <DiffField
        label="Description (HTML)"
        state={!c.body_html ? "synced" : fieldState(c.body_html, storeItem?.shopify_body_html)}
        hint={
          !c.body_html
            ? "Not set — Shopify's description kept as-is"
            : fieldState(c.body_html, storeItem?.shopify_body_html) === "drifted"
              ? "Shopify has a different description"
              : undefined
        }
        value={
          c.body_html
            ? <span className="text-xs text-muted-foreground line-clamp-2" dangerouslySetInnerHTML={{ __html: c.body_html }} />
            : <span className="text-muted-foreground italic">Using Shopify's description</span>
        }
      />

      {/* Tags */}
      <DiffField
        label="Tags"
        state={tagsState()}
        hint={tagsState() === "drifted" ? `Shopify: ${(storeItem?.shopify_tags ?? []).join(", ") || "none"}` : undefined}
        value={
          c.tags?.length
            ? <div className="flex flex-wrap gap-1 mt-1">{c.tags.map(t => <Badge key={t} variant="secondary" className="text-xs font-normal">{t}</Badge>)}</div>
            : <span className="text-muted-foreground italic text-xs">No tags</span>
        }
      />

      {/* Collections */}
      <CollectionsSelector curatedId={c.id} shopifyProductId={c.shopify_product_id} />

      {/* SEO — only synced when locally set; empty local = keep Shopify's */}
      <DiffField
        label="SEO title"
        state={!c.seo_title ? "synced" : fieldState(c.seo_title, storeItem?.shopify_seo_title)}
        hint={
          !c.seo_title
            ? "Not set — Shopify's SEO title kept as-is"
            : fieldState(c.seo_title, storeItem?.shopify_seo_title) === "drifted"
              ? `Shopify: ${storeItem?.shopify_seo_title || "none"}`
              : undefined
        }
        value={c.seo_title || <span className="text-muted-foreground italic">Using Shopify's SEO title</span>}
      />
      <DiffField
        label="SEO description"
        state={!c.seo_description ? "synced" : fieldState(c.seo_description, storeItem?.shopify_seo_description)}
        hint={
          !c.seo_description
            ? "Not set — Shopify's SEO description kept as-is"
            : fieldState(c.seo_description, storeItem?.shopify_seo_description) === "drifted"
              ? `Shopify: ${storeItem?.shopify_seo_description || "none"}`
              : undefined
        }
        value={c.seo_description || <span className="text-muted-foreground italic">Using Shopify's SEO description</span>}
      />

      {/* Inventory warning */}
      {effectiveQty === 0 && (
        <div className="flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-600">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          Zero inventory — Shopify status will be set to <strong>Draft</strong> on sync. Auto-restores to Active when stock returns.
        </div>
      )}

      {/* Shopify Admin link */}
      {c.shopify_product_id && (
        <a
          href={shopifyAdminBase ? `${shopifyAdminBase}/products/${c.shopify_product_id}` : "#"}
          target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs text-blue-500 hover:underline"
        >
          <ExternalLink className="h-3 w-3" />
          Open in Shopify Admin #{c.shopify_product_id}
        </a>
      )}
    </div>
  );
}

// ── Curated Queue ─────────────────────────────────────────────────────────────

type QueueTab = "all" | "wip" | "ready" | "draft" | "published";

const STATE_BADGE: Record<CuratedProduct["status"], string> = {
  wip:       "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30",
  draft:     "bg-zinc-500/20 text-zinc-400 border border-zinc-500/30",
  ready:     "bg-blue-500/20 text-blue-400 border border-blue-500/30",
  published: "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30",
  archived:  "bg-muted/30 text-muted-foreground border border-border/40",
};

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 2) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function CuratedQueue() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<QueueTab>("all");

  const { data, isLoading } = useQuery({
    queryKey: ["curated-queue"],
    queryFn: () => api.curatedList(),
    staleTime: 30_000,
  });

  const items = data?.items ?? [];
  const visible = (tab === "all" ? items : items.filter(i => i.status === tab))
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
    .slice(0, 50);

  const counts: Record<QueueTab, number> = {
    all:       items.length,
    wip:       items.filter(i => i.status === "wip").length,
    draft:     items.filter(i => i.status === "draft").length,
    ready:     items.filter(i => i.status === "ready").length,
    published: items.filter(i => i.status === "published").length,
  };

  const TABS: { key: QueueTab; label: string }[] = [
    { key: "all",       label: "All" },
    { key: "wip",       label: "WIP" },
    { key: "ready",     label: "Ready" },
    { key: "draft",     label: "Shopify Draft" },
    { key: "published", label: "Published" },
  ];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            Curated Queue
            {items.length > 0 && (
              <span className="text-xs font-normal px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground tabular-nums">
                {items.length}
              </span>
            )}
          </CardTitle>
          {/* Tab filters */}
          <div className="flex gap-1">
            {TABS.map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-2.5 py-1 rounded text-xs border transition-colors ${
                  tab === t.key
                    ? "bg-accent text-accent-foreground border-border"
                    : "border-transparent text-muted-foreground hover:bg-accent/50"
                }`}
              >
                {t.label}
                {counts[t.key] > 0 && (
                  <span className="ml-1 tabular-nums text-[10px] opacity-70">{counts[t.key]}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <div className="flex items-center gap-2 py-6 text-muted-foreground text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />Loading queue…
          </div>
        ) : visible.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No curated products yet
          </div>
        ) : (
          <div className="divide-y divide-border/40">
            {visible.map(item => (
              <div
                key={item.id}
                className="flex items-center gap-3 py-2.5 group"
              >
                {/* State badge */}
                <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded font-medium ${STATE_BADGE[item.status]}`}>
                  {item.status}
                </span>

                {/* Title + SKU */}
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate leading-snug">
                    {item.title ?? <span className="italic text-muted-foreground">Untitled</span>}
                  </div>
                  <div className="text-xs font-mono text-muted-foreground mt-0.5">{item.tdsynnex_sku}</div>
                </div>

                {/* Updated at */}
                <div className="shrink-0 flex items-center gap-1 text-[11px] text-muted-foreground tabular-nums">
                  <Clock className="h-3 w-3 opacity-50" />
                  {relativeTime(item.updated_at)}
                </div>

                {/* Edit button */}
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2.5 text-xs opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                  onClick={() => navigate(`/curated/${item.id}`)}
                >
                  Edit
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

export function CurationPage() {
  // Two entry points:
  //   /curate/:sku  — comes from catalog (SKU known, curated draft may not exist yet)
  //   /curated/:id  — comes from "Edit" on a draft (curated UUID known)
  const { sku: skuParam, id: curatedIdParam } = useParams<{ sku?: string; id?: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [skuInput, setSkuInput] = useState(skuParam ?? "");
  const [activeTab, setActiveTab] = useState("wip");

  // When entering via /curated/:id, load curated detail first to get the SKU.
  const curatedByUUID = useQuery({
    queryKey: ["curated-detail", curatedIdParam],
    queryFn: () => api.curatedDetail(curatedIdParam!),
    enabled: !!curatedIdParam && !skuParam,
    retry: false,
  });

  // Resolved SKU — either from the URL param or from the UUID-loaded curated record.
  const sku = skuParam ?? curatedByUUID.data?.tdsynnex_sku ?? "";

  // Sync the search box when sku resolves from UUID path.
  useEffect(() => {
    if (sku && !skuInput) setSkuInput(sku);
  }, [sku]);

  const product = useQuery({
    queryKey: ["product", sku],
    queryFn: () => api.productDetail(sku),
    enabled: !!sku,
    retry: false,
    staleTime: 60_000,
  });

  // Find curated draft by SKU (only needed when entering via /curate/:sku).
  // When entering via /curated/:id we already have the record.
  const curatedBySKU = useQuery({
    queryKey: ["curated-by-sku", sku],
    queryFn: async () => {
      try {
        const all = await api.curatedList();
        return all.items.find((c) => c.tdsynnex_sku === sku) ?? null;
      } catch {
        return null;
      }
    },
    enabled: !!sku && !curatedIdParam,
  });

  // Unified curated record regardless of entry point.
  const curated = curatedIdParam ? curatedByUUID : curatedBySKU;

  useQuery({
    queryKey: ["curated-detail-full", curated.data?.id],
    queryFn: () => api.curatedDetail(curated.data!.id),
    enabled: !!curated.data?.id && !curatedIdParam,
  });

  const pricing = useQuery({
    queryKey: ["pricing", sku],
    queryFn: () => api.pricingPropose(sku),
    enabled: !!sku,
    retry: false,
    staleTime: 60_000,
  });

  const invalidateCurated = () => {
    qc.invalidateQueries({ queryKey: ["curated-by-sku", sku] });
    qc.invalidateQueries({ queryKey: ["curated-detail", curatedIdParam] });
  };

  const [editMode, setEditMode] = useState(false);
  const [editDraft, setEditDraft] = useState<{
    title: string; body_html: string; seo_title: string; seo_description: string;
    tags: string; vendor: string; product_type: string;
  } | null>(null);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");

  const startEdit = (c: CuratedProduct) => {
    setEditDraft({
      title: c.title ?? "",
      body_html: c.body_html ?? "",
      seo_title: c.seo_title ?? "",
      seo_description: c.seo_description ?? "",
      tags: c.tags?.join(", ") ?? "",
      vendor: c.vendor ?? "",
      product_type: c.product_type ?? "",
    });
    setEditMode(true);
    setSaveStatus("idle");
  };

  const saveEdit = async (c: CuratedProduct) => {
    if (!editDraft) return;
    setSaveStatus("saving");
    try {
      await api.curatedUpdate(c.id, {
        title: editDraft.title || undefined,
        body_html: editDraft.body_html || undefined,
        seo_title: editDraft.seo_title || undefined,
        seo_description: editDraft.seo_description || undefined,
        tags: editDraft.tags ? editDraft.tags.split(",").map(t => t.trim()).filter(Boolean) : undefined,
        vendor: editDraft.vendor || undefined,
        product_type: editDraft.product_type || undefined,
      });
      setSaveStatus("saved");
      setEditMode(false);
      setEditDraft(null);
      invalidateCurated();
      toast.success("Saved locally — Re-sync to Shopify to push changes");
    } catch {
      setSaveStatus("error");
      toast.error("Save failed — check your connection");
    }
  };

  const [curateLog, setCurateLog] = useState<{ text: string }[]>([]);
  const [curateStatus, setCurateStatus] = useState<"idle" | "running" | "done" | "error">("idle");
  const [seedContext, setSeedContext] = useState("");
  const [pendingDraft, setPendingDraft] = useState<{
    title: string; body_html: string; seo_title: string; seo_description: string; tags: string[];
    suggested_collection_ids?: number[];
  } | null>(null);
  const [commitStatus, setCommitStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");

  // Restore pending draft on SKU change: Redis first, localStorage fallback.
  useEffect(() => {
    if (!sku) return;
    setPendingDraft(null);
    setCommitStatus("idle");

    const lsKey = `draft:${sku}`;
    api.curateDraft(sku).then(r => {
      if (r.found && r.draft) {
        setPendingDraft(r.draft as typeof pendingDraft);
        setCurateStatus("done");
        // Keep localStorage in sync.
        try { localStorage.setItem(lsKey, JSON.stringify(r.draft)); } catch { /* ignore */ }
      } else {
        // Redis miss — try localStorage.
        try {
          const raw = localStorage.getItem(lsKey);
          if (raw) {
            const cached = JSON.parse(raw);
            setPendingDraft(cached);
            setCurateStatus("done");
          }
        } catch { /* ignore */ }
      }
    }).catch(() => {
      // agents-py unreachable — fall back to localStorage.
      try {
        const raw = localStorage.getItem(lsKey);
        if (raw) {
          const cached = JSON.parse(raw);
          setPendingDraft(cached);
          setCurateStatus("done");
        }
      } catch { /* ignore */ }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sku]);

  const runCurateStream = useCallback(() => {
    if (!sku || curateStatus === "running") return;
    setCurateLog([]);
    setCurateStatus("running");
    setPendingDraft(null);
    setCommitStatus("idle");

    const params = seedContext.trim() ? `?seed=${encodeURIComponent(seedContext.trim())}` : "";
    const es = new EventSource(`/api/curate/${encodeURIComponent(sku)}/stream${params}`);
    es.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data) as { type: string; text?: string; result?: Record<string, unknown> };
        if (msg.type === "step") {
          setCurateLog(l => [...l, { text: msg.text ?? "" }]);
        } else if (msg.type === "done") {
          setCurateLog(l => [...l, { text: "Draft ready — click Save to commit." }]);
          setCurateStatus("done");
          es.close();
          if (msg.result) {
            const draft = msg.result as typeof pendingDraft;
            setPendingDraft(draft);
            // Persist to localStorage so draft survives refresh if Redis/agents-py are down.
            try { localStorage.setItem(`draft:${sku}`, JSON.stringify(draft)); } catch { /* ignore */ }
          }
          setActiveTab("wip");
        } else if (msg.type === "error") {
          setCurateLog(l => [...l, { text: `Error: ${msg.text ?? "unknown"}` }]);
          setCurateStatus("error");
          es.close();
        }
      } catch { /* ignore parse errors */ }
    };
    es.onerror = () => {
      setCurateLog(l => [...l, { text: "Connection lost — is agents-py running?" }]);
      setCurateStatus("error");
      es.close();
    };
  }, [sku, curateStatus]);

  const saveDraft = useCallback(async () => {
    if (!sku || !pendingDraft) return;
    setCommitStatus("saving");
    try {
      await api.curateCommit(sku, pendingDraft);
      setCommitStatus("saved");
      setPendingDraft(null);
      // Clear localStorage cache — draft is now in Postgres.
      try { localStorage.removeItem(`draft:${sku}`); } catch { /* ignore */ }
      invalidateCurated();
    } catch {
      setCommitStatus("error");
    }
  }, [sku, pendingDraft]);

  const patchMut = useMutation({
    mutationFn: (patch: Parameters<typeof api.curatedUpdate>[1]) =>
      api.curatedUpdate(curated.data!.id, patch),
    onSuccess: () => { invalidateCurated(); toast.success("Saved"); },
    onError: () => toast.error("Save failed"),
  });

  const stateMut = useMutation({
    mutationFn: ({ id, state }: { id: string; state: CuratedState }) =>
      api.patchCuratedState(id, state),
    onSuccess: (_, { state }) => {
      invalidateCurated();
      toast.success(`State → ${state}`);
    },
    onError: () => toast.error("State change failed"),
  });

  const publishMut = useMutation({
    mutationFn: (id: string) => api.publish(id),
    onSuccess: (data) => {
      invalidateCurated();
      qc.invalidateQueries({ queryKey: ["storeProducts"] });
      toast.success(`Synced to Shopify — product #${data.shopify_product_id}`);
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? String(err.body ?? err.message) : "Publish failed";
      toast.error(msg, { duration: 8000 });
    },
  });

  const pullFromShopifyMut = useMutation({
    mutationFn: (id: string) => fetch(`/api/curated/${id}/pull`, { method: "POST" }).then(r => { if (!r.ok) throw new Error("pull failed"); }),
    onSuccess: () => {
      invalidateCurated();
      toast.success("Pulled from Shopify — local copy updated");
    },
    onError: () => toast.error("Pull from Shopify failed"),
  });

  const delistMut = useMutation({
    mutationFn: (id: string) => api.delist(id),
    onSuccess: (data) => {
      invalidateCurated();
      qc.invalidateQueries({ queryKey: ["storeProducts"] });
      const shopifyMsg = data.shopify === "archived_in_shopify"
        ? "archived locally + delisted from Shopify"
        : data.shopify === "no_shopify_id"
        ? "archived locally (no Shopify link)"
        : `archived locally — Shopify: ${data.shopify}`;
      toast.success(`Delisted — ${shopifyMsg}`);
    },
    onError: () => toast.error("Delist failed — archived locally as fallback"),
  });

  const p = product.data ?? null;
  const c = curated.data;

  // When loaded via UUID and TD SYNNEX product is gone (removed from feed),
  // use a minimal stub so the curated workspace still renders.
  const tdProduct: TdsynnexProduct = p ?? {
    tdsynnex_sku: c?.tdsynnex_sku ?? "",
    trading_partner_code: "", manufacturer_part_number: "", upc_code: null,
    manufacturer_name: "", brand_slug: "", short_description: "",
    contract_price: null, msrp: null, map_price: null,
    qty_on_hand: 0, status: "?", unspsc_code: null, category_code: null,
    sku_attributes: null, warehouse_qty: null, margin_pct: null,
    ship_weight: null, length: null, width: null, height: null, parcel_shippable: null,
    curated_id: c?.id ?? null, curated_state: c?.status ?? null, shopify_product_id: c?.shopify_product_id ?? null,
  };

  // Load store products lazily for Shopify Preview diff highlighting.
  const storeProducts = useQuery({
    queryKey: ["storeProducts"],
    queryFn: () => api.storeProducts(),
    enabled: !!c?.shopify_product_id,
    staleTime: 60_000,
  });
  const storeItem = storeProducts.data?.items.find(s => s.id === c?.id) ?? null;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Curate</h1>
          <p className="text-muted-foreground text-sm mt-1">
            AI-powered Shopify listing builder — SEO, images, pricing in one workspace.
          </p>
        </div>
        {c && (
          <TooltipProvider delayDuration={400}>
            <div className="flex items-center gap-2">
              {c.status === "wip" && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="sm" variant="outline"
                      onClick={() => stateMut.mutate({ id: c.id, state: "ready" })}
                      disabled={stateMut.isPending}>
                      <Check className="h-3.5 w-3.5 mr-1" />Mark ready
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    Marks this draft as reviewed and ready to publish. Does not push to Shopify yet.
                  </TooltipContent>
                </Tooltip>
              )}
              {c.status === "ready" && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="sm" variant="outline"
                      onClick={() => stateMut.mutate({ id: c.id, state: "wip" })}
                      disabled={stateMut.isPending}>
                      <X className="h-3.5 w-3.5 mr-1" />Back to draft
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    Returns the listing to draft state for further edits. Does not affect Shopify.
                  </TooltipContent>
                </Tooltip>
              )}
              {c.status !== "archived" && (
                <>
                  {!c.shopify_product_id && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button size="sm" variant="outline"
                          onClick={() => stateMut.mutate({ id: c.id, state: "archived" })}
                          disabled={stateMut.isPending}>
                          Archive locally
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        Archives in Symphony only — product was never published to Shopify.
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {c.shopify_product_id && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button size="sm" variant="outline"
                          className="border-rose-500/50 text-rose-400 hover:bg-rose-500/10"
                          onClick={async () => {
                            try {
                              await delistMut.mutateAsync(c.id);
                            } catch {
                              stateMut.mutate({ id: c.id, state: "archived" });
                            }
                          }}
                          disabled={delistMut.isPending || stateMut.isPending}>
                          {delistMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                          Delist from Shopify
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        Archives locally AND sets Shopify to ARCHIVED — product disappears from the store. Use for discontinued or no-longer-available items.
                      </TooltipContent>
                    </Tooltip>
                  )}
                </>
              )}
              {c.shopify_product_id && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={() => pullFromShopifyMut.mutate(c.id)}
                      disabled={pullFromShopifyMut.isPending}
                      variant="outline"
                      size="sm"
                    >
                      {pullFromShopifyMut.isPending
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                        : <RefreshCcw className="h-3.5 w-3.5 mr-1" />}
                      Pull from Shopify
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    Fetches the current Shopify product state and overwrites local title, description, price, status, and tags. Use after making changes directly in Shopify admin.
                  </TooltipContent>
                </Tooltip>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => publishMut.mutate(c.id)}
                    disabled={publishMut.isPending}
                    variant="ember"
                    size="sm"
                  >
                    <Upload className="h-3.5 w-3.5 mr-1" />
                    {publishMut.isPending ? "Publishing…" : c.shopify_product_id ? "Re-sync to Shopify" : "Publish to Shopify"}
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  {c.shopify_product_id
                    ? "Pushes the latest title, description, price, tags, and new images to Shopify. Existing Shopify images are skipped."
                    : "Creates a new product in Shopify with the current title, description, vendor, tags, price, and images."}
                </TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        )}
      </div>

      {/* SKU lookup */}
      <Card>
        <CardContent className="pt-4">
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (skuInput.trim()) navigate(`/curate/${encodeURIComponent(skuInput.trim())}`);
            }}
          >
            <Input
              placeholder="TD SYNNEX SKU or MPN — e.g. 12345678"
              value={skuInput}
              onChange={(e) => setSkuInput(e.target.value)}
              className="font-mono"
            />
            <Button type="submit">Load</Button>
          </form>
        </CardContent>
      </Card>

      {!sku && <CuratedQueue />}

      {curatedIdParam && curatedByUUID.isLoading && (
        <Card>
          <CardContent className="flex items-center gap-2 py-8 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading draft…
          </CardContent>
        </Card>
      )}

      {curatedIdParam && curatedByUUID.isError && (
        <Card>
          <CardContent className="flex items-center gap-2 py-8 text-destructive">
            <AlertTriangle className="h-4 w-4" />
            Draft not found.
          </CardContent>
        </Card>
      )}

      {sku && product.isError && !curatedIdParam && (
        <Card>
          <CardContent className="flex items-center gap-2 py-8 text-destructive">
            <AlertTriangle className="h-4 w-4" />
            {product.error instanceof ApiError && product.error.status === 404
              ? "SKU not found. Upload a TD SYNNEX feed first."
              : "Failed to load SKU."}
          </CardContent>
        </Card>
      )}

      {(p || (curatedIdParam && c)) && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
          {/* Left: workspace */}
          <div className="space-y-4">
            {/* Discontinued banner — TD SYNNEX product no longer in feed */}
            {!p && c && (
              <div className="flex items-start gap-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3">
                <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
                    Product discontinued at TD SYNNEX
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    SKU <span className="font-mono">{c.tdsynnex_sku}</span> is no longer in the TD SYNNEX feed.
                    {c.shopify_product_id
                      ? " It's still live on Shopify — delist it when you're ready to stop selling."
                      : " It has never been published to Shopify."}
                  </p>
                </div>
              </div>
            )}

            {/* Status bar */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-mono text-muted-foreground">{tdProduct.tdsynnex_sku}</span>
              <Badge variant="outline" className="font-normal">{tdProduct.brand_slug}</Badge>
              {tdProduct.manufacturer_part_number && (
                <span className="text-xs font-mono bg-muted px-2 py-0.5 rounded text-muted-foreground" title="Shopify variant SKU">
                  SKU: {tdProduct.manufacturer_part_number}
                </span>
              )}
              {c ? <StatusBadge status={c.status} /> : <Badge variant="outline">not drafted</Badge>}
              {c?.shopify_product_id && (
                <Badge variant="success" className="font-normal text-xs">
                  Shopify #{c.shopify_product_id}
                </Badge>
              )}
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="w-full justify-start">
                <TabsTrigger value="draft">Draft Content</TabsTrigger>
                <TabsTrigger value="images">
                  Images
                  {c && (() => {
                    // images count badge loaded elsewhere
                    return null;
                  })()}
                </TabsTrigger>
                <TabsTrigger value="seo">SEO Preview</TabsTrigger>
                <TabsTrigger value="pricing">Pricing</TabsTrigger>
                {c && <TabsTrigger value="shopify-preview">Shopify Preview</TabsTrigger>}
              </TabsList>

              {/* ── Draft Content ── */}
              <TabsContent value="draft" className="space-y-4 pt-4">
                {c ? (
                  <>
                    {/* Edit / Save toolbar */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        {saveStatus === "saved" && <span className="text-emerald-500">Saved locally</span>}
                        {saveStatus === "error" && <span className="text-rose-500">Save failed</span>}
                      </span>
                      <div className="flex gap-2">
                        {editMode ? (
                          <>
                            <Button size="sm" variant="outline" onClick={() => { setEditMode(false); setEditDraft(null); }}>
                              Cancel
                            </Button>
                            <Button size="sm" variant="ember" onClick={() => saveEdit(c)} disabled={saveStatus === "saving"}>
                              {saveStatus === "saving" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Check className="h-3 w-3 mr-1" />}
                              Save locally
                            </Button>
                          </>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => startEdit(c)}>
                            <Pencil className="h-3 w-3 mr-1" /> Edit
                          </Button>
                        )}
                      </div>
                    </div>

                    {editMode && editDraft ? (
                      /* ── Edit mode ── */
                      <div className="space-y-4">
                        {tdProduct.qty_on_hand === 0 && (
                          <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-600">
                            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                            <span>
                              <strong>Zero inventory</strong> — when synced, Shopify status will be set to <strong>Draft</strong> (not archived) so the listing auto-restores when stock returns. Save locally, then Re-sync to Shopify to apply.
                            </span>
                          </div>
                        )}
                        <div className="space-y-1">
                          <label className="text-xs uppercase tracking-wide text-muted-foreground">Title</label>
                          <input className="w-full rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500"
                            value={editDraft.title} onChange={e => setEditDraft(d => d && ({ ...d, title: e.target.value }))} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <label className="text-xs uppercase tracking-wide text-muted-foreground">Vendor</label>
                            <input className="w-full rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500"
                              value={editDraft.vendor} onChange={e => setEditDraft(d => d && ({ ...d, vendor: e.target.value }))} />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs uppercase tracking-wide text-muted-foreground">Product Type</label>
                            <input className="w-full rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500"
                              value={editDraft.product_type} onChange={e => setEditDraft(d => d && ({ ...d, product_type: e.target.value }))} />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs uppercase tracking-wide text-muted-foreground">Tags <span className="text-muted-foreground/60">(comma-separated)</span></label>
                          <input className="w-full rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500"
                            value={editDraft.tags} onChange={e => setEditDraft(d => d && ({ ...d, tags: e.target.value }))} />
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between">
                            <label className="text-xs uppercase tracking-wide text-muted-foreground">SEO Title</label>
                            {charCount(editDraft.seo_title, 70)}
                          </div>
                          <input className="w-full rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500"
                            value={editDraft.seo_title} onChange={e => setEditDraft(d => d && ({ ...d, seo_title: e.target.value }))} />
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between">
                            <label className="text-xs uppercase tracking-wide text-muted-foreground">SEO Description</label>
                            {charCount(editDraft.seo_description, 160)}
                          </div>
                          <textarea rows={3} className="w-full rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500 resize-none"
                            value={editDraft.seo_description} onChange={e => setEditDraft(d => d && ({ ...d, seo_description: e.target.value }))} />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs uppercase tracking-wide text-muted-foreground">Body HTML</label>
                          <textarea rows={12} className="w-full rounded-md border border-border bg-muted/30 px-3 py-2 text-xs font-mono text-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500 resize-y"
                            value={editDraft.body_html} onChange={e => setEditDraft(d => d && ({ ...d, body_html: e.target.value }))} />
                        </div>
                      </div>
                    ) : (
                      /* ── Read mode ── */
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <FieldBox label="Vendor" value={c.vendor} />
                          <FieldBox label="Product Type" value={c.product_type} />
                        </div>
                        <FieldBox label="Title" value={c.title} hint={charCount(c.title, 60)} mono={false} />
                        {c.body_html && (
                          <div className="space-y-1">
                            <span className="text-xs uppercase tracking-wide text-muted-foreground">Body HTML</span>
                            <div
                              className="prose prose-sm prose-invert max-w-none rounded-md border border-border bg-muted/30 p-4 text-sm
                                [&_table]:w-full [&_table]:text-xs [&_th]:text-left [&_th]:py-1 [&_td]:py-1
                                [&_th]:border-b [&_th]:border-border [&_tr]:border-b [&_tr]:border-border/40
                                [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1
                                [&_ul]:list-disc [&_ul]:pl-4 [&_li]:mb-0.5"
                              dangerouslySetInnerHTML={{ __html: c.body_html }}
                            />
                            <details className="text-xs">
                              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">View raw HTML</summary>
                              <FieldBox label="" value={c.body_html} multiline mono />
                            </details>
                          </div>
                        )}
                        <FieldBox label="SEO Title" value={c.seo_title} hint={charCount(c.seo_title, 70)} />
                        <FieldBox label="SEO Description" value={c.seo_description} hint={charCount(c.seo_description, 160)} multiline />
                        <FieldBox label="Tags" value={c.tags?.join(", ") ?? ""} />
                      </>
                    )}
                  </>
                ) : pendingDraft ? (
                  // Pending draft from AI — not yet saved to DB
                  <div className="flex flex-col gap-4">
                    <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-medium text-amber-600 uppercase tracking-wide">
                          Unsaved AI draft — review &amp; save
                        </p>
                        <div className="flex gap-2">
                          <Button
                            onClick={saveDraft}
                            disabled={commitStatus === "saving"}
                            variant="ember"
                            size="sm"
                          >
                            {commitStatus === "saving"
                              ? <Loader2 className="h-4 w-4 animate-spin" />
                              : null}
                            {commitStatus === "saving" ? "Saving…" : "Save Draft"}
                          </Button>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Seed context — paste a URL, notes, or specs to guide the LLM…"
                          value={seedContext}
                          onChange={(e) => setSeedContext(e.target.value)}
                          className="flex-1 text-xs px-3 py-2 rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-ring"
                        />
                        <Button
                          onClick={runCurateStream}
                          disabled={curateStatus === "running"}
                          variant="outline"
                          size="sm"
                        >
                          {curateStatus === "running"
                            ? <Loader2 className="h-4 w-4 animate-spin" />
                            : <Sparkles className="h-4 w-4" />}
                          {curateStatus === "running" ? "Generating…" : "Regenerate"}
                        </Button>
                      </div>
                      <FieldBox label="Title" value={pendingDraft.title} />
                      <FieldBox label="SEO Title" value={pendingDraft.seo_title} hint={charCount(pendingDraft.seo_title, 70)} />
                      <FieldBox label="SEO Description" value={pendingDraft.seo_description} hint={charCount(pendingDraft.seo_description, 160)} multiline />
                      <FieldBox label="Tags" value={pendingDraft.tags.join(", ")} />
                      {pendingDraft.body_html && (
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground uppercase tracking-wide">Body HTML preview</p>
                          <div
                            className="prose prose-sm max-w-none text-foreground rounded border border-border bg-background p-3 max-h-64 overflow-y-auto text-xs"
                            dangerouslySetInnerHTML={{ __html: pendingDraft.body_html }}
                          />
                        </div>
                      )}
                      {commitStatus === "error" && (
                        <p className="text-xs text-destructive">Save failed — check network and try again.</p>
                      )}
                    </div>
                    {curateLog.length > 0 && (
                      <div className="rounded-md border border-border bg-muted/30 p-3 text-xs font-mono space-y-1">
                        {curateLog.map((s, i) => (
                          <div key={i} className={`flex gap-2 ${curateStatus === "error" && i === curateLog.length - 1 ? "text-destructive" : "text-muted-foreground"}`}>
                            <span className="opacity-40">{String(i + 1).padStart(2, "0")}</span>
                            <span>{s.text}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center gap-4 py-10 text-muted-foreground rounded-lg border-2 border-dashed border-border">
                    <Sparkles className="h-8 w-8 opacity-40" />
                    <p className="text-sm">No draft yet — generate one with AI.</p>
                    <div className="w-full max-w-lg flex gap-2">
                      <input
                        type="text"
                        placeholder="Seed context — paste a URL, notes, or specs to guide the LLM…"
                        value={seedContext}
                        onChange={(e) => setSeedContext(e.target.value)}
                        className="flex-1 text-xs px-3 py-2 rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-ring"
                      />
                      <Button
                        onClick={runCurateStream}
                        disabled={curateStatus === "running"}
                        variant="ember"
                        size="sm"
                      >
                        {curateStatus === "running"
                          ? <Loader2 className="h-4 w-4 animate-spin" />
                          : <Sparkles className="h-4 w-4" />}
                        {curateStatus === "running" ? "Generating…" : "Generate"}
                      </Button>
                    </div>
                    {curateLog.length > 0 && (
                      <div className="w-full max-w-lg rounded-md border border-border bg-muted/30 p-3 text-xs font-mono space-y-1">
                        {curateLog.map((s, i) => (
                          <div key={i} className={`flex gap-2 ${curateStatus === "error" && i === curateLog.length - 1 ? "text-destructive" : "text-muted-foreground"}`}>
                            <span className="opacity-40">{String(i + 1).padStart(2, "0")}</span>
                            <span>{s.text}</span>
                          </div>
                        ))}
                        {curateStatus === "running" && (
                          <div className="flex gap-2 text-muted-foreground animate-pulse">
                            <span className="opacity-40">··</span><span>Waiting for Claude…</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>

              {/* ── Images ── */}
              <TabsContent value="images" className="pt-4">
                {c ? (
                  <ImageGallery
                    curatedId={c.id}
                    shopifyProductId={c.shopify_product_id}
                    mpn={tdProduct.manufacturer_part_number ?? ""}
                    brand={tdProduct.brand_slug ?? ""}
                    description={tdProduct.short_description ?? ""}
                    title={c.title ?? ""}
                  />
                ) : (
                  <div className="text-sm text-muted-foreground py-8 text-center">
                    Generate a draft first to enable image management.
                  </div>
                )}
              </TabsContent>

              {/* ── SEO Preview ── */}
              <TabsContent value="seo" className="pt-4 space-y-4">
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Google snippet preview</p>
                  {seoPreview(c?.seo_title ?? null, c?.seo_description ?? null)}
                </div>
                {c && (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">SEO Title</div>
                      <div className="flex items-center justify-between">
                        <span>{(c.seo_title ?? "").length} chars</span>
                        {charCount(c.seo_title, 70)}
                      </div>
                      <div className="mt-1 h-1.5 rounded-full bg-border overflow-hidden">
                        <div
                          className="h-full bg-emerald-500 transition-all"
                          style={{ width: `${Math.min(100, ((c.seo_title ?? "").length / 70) * 100)}%` }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">SEO Description</div>
                      <div className="flex items-center justify-between">
                        <span>{(c.seo_description ?? "").length} chars</span>
                        {charCount(c.seo_description, 160)}
                      </div>
                      <div className="mt-1 h-1.5 rounded-full bg-border overflow-hidden">
                        <div
                          className="h-full bg-emerald-500 transition-all"
                          style={{ width: `${Math.min(100, ((c.seo_description ?? "").length / 160) * 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Tags</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(c?.tags ?? []).map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-xs font-normal">
                        {tag}
                      </Badge>
                    ))}
                    {(!c?.tags?.length) && (
                      <span className="text-xs text-muted-foreground italic">No tags yet</span>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* ── Pricing ── */}
              <TabsContent value="pricing" className="pt-4">
                <PricingTab
                  pricing={pricing.data ?? null}
                  pricingLoading={pricing.isLoading}
                  pricingError={pricing.isError}
                  curatedId={c?.id}
                  savedPrice={c?.proposed_price ?? null}
                  savedCompareAt={c?.compare_at_price ?? null}
                  savedCompetitors={c?.competitor_prices ?? []}
                  msrp={tdProduct.msrp ? Number(tdProduct.msrp) : null}
                  tdProduct={tdProduct}
                  onRefresh={() => pricing.refetch()}
                  onApply={(price, compareAt, competitors) =>
                    patchMut.mutateAsync({
                      proposed_price: price,
                      compare_at_price: compareAt ?? undefined,
                      competitor_prices: JSON.stringify(competitors) as any,
                    })
                  }
                  applying={patchMut.isPending}
                />
              </TabsContent>

              {/* ── Shopify Preview ── */}
              {c && (
                <TabsContent value="shopify-preview" className="pt-4">
                  <ShopifyPreview c={c} p={tdProduct} storeItem={storeItem} />
                </TabsContent>
              )}
            </Tabs>

            {/* Generate / Regenerate CTA */}
            {c && (
              <div className="flex flex-col gap-2 pt-2">
                <div className="flex gap-2 flex-wrap items-center">
                  <input
                    type="text"
                    placeholder="Seed context — paste a URL, notes, or specs…"
                    value={seedContext}
                    onChange={(e) => setSeedContext(e.target.value)}
                    className="flex-1 min-w-[200px] text-xs px-3 py-2 rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                  <Button
                    onClick={runCurateStream}
                    disabled={curateStatus === "running"}
                    variant="outline"
                    size="sm"
                  >
                    {curateStatus === "running"
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <Sparkles className="h-4 w-4" />}
                    {curateStatus === "running" ? "Generating…" : "Regenerate with AI"}
                  </Button>
                  {pendingDraft && (
                    <Button
                      onClick={saveDraft}
                      disabled={commitStatus === "saving"}
                      variant="ember"
                      size="sm"
                    >
                      {commitStatus === "saving" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      {commitStatus === "saving" ? "Saving…" : "Save new draft"}
                    </Button>
                  )}
                </div>
                {curateLog.length > 0 && (
                  <div className="rounded-md border border-border bg-muted/30 p-3 text-xs font-mono space-y-1">
                    {curateLog.map((s, i) => (
                      <div key={i} className={`flex gap-2 ${curateStatus === "error" && i === curateLog.length - 1 ? "text-destructive" : "text-muted-foreground"}`}>
                        <span className="opacity-40">{String(i + 1).padStart(2, "0")}</span>
                        <span>{s.text}</span>
                      </div>
                    ))}
                    {curateStatus === "running" && (
                      <div className="flex gap-2 text-muted-foreground animate-pulse">
                        <span className="opacity-40">··</span><span>Waiting for Claude…</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right: source panel */}
          <Card className="h-fit">
            <CardHeader>
              <CardTitle className="text-sm flex items-center justify-between">
                <span>TD SYNNEX Source</span>
                <Badge variant="outline" className="font-mono text-xs">{tdProduct.tdsynnex_sku}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {/* Identity */}
              <SourceField label="Shopify SKU (MPN)" value={tdProduct.manufacturer_part_number} mono />
              <SourceField label="UPC" value={tdProduct.upc_code} mono />
              <SourceField label="UNSPSC" value={tdProduct.unspsc_code} mono />
              <SourceField label="Manufacturer" value={tdProduct.manufacturer_name} />
              <SourceField label="Trading partner" value={tdProduct.trading_partner_code} mono />

              {/* Availability */}
              <SourceField
                label="TD SYNNEX status"
                value={tdProduct.status}
                hint={tdProduct.status !== "A" ? <span className="text-amber-500 text-[10px]">⚠ not active at distributor</span> : undefined}
              />
              <SourceField
                label="SKU attributes"
                value={tdProduct.sku_attributes || "—"}
                hint={<span className="text-[10px] text-muted-foreground/60">{tdProduct.sku_attributes?.[0] === 'Y' ? "Drop-ship eligible" : "Physical stock"}</span>}
              />

              {/* Pricing */}
              <SourceField label="Cost" value={tdProduct.contract_price ? formatUsd(Number(tdProduct.contract_price)) : null} />
              <SourceField label="MAP" value={tdProduct.map_price ? formatUsd(Number(tdProduct.map_price)) : null} />
              <SourceField label="MSRP" value={tdProduct.msrp ? formatUsd(Number(tdProduct.msrp)) : null} />
              {tdProduct.margin_pct != null && (
                <SourceField label="Margin at cost" value={`${tdProduct.margin_pct.toFixed(1)}%`} />
              )}

              {/* Inventory */}
              {c && <QtyOverrideField c={c} p={tdProduct} patchMut={patchMut} />}
              {tdProduct.warehouse_qty && Object.keys(tdProduct.warehouse_qty).length > 0 && (
                <div className="space-y-1">
                  <p className="text-[10px] font-medium tracking-widest uppercase text-muted-foreground/60">Warehouse breakdown</p>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(tdProduct.warehouse_qty).map(([wh, qty]) => (
                      <span key={wh} className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-muted border border-border/50">
                        {wh}: <span className="tabular-nums font-medium">{qty}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Shipping */}
              {(tdProduct.ship_weight || tdProduct.length || tdProduct.width || tdProduct.height) && (
                <div className="space-y-1">
                  <p className="text-[10px] font-medium tracking-widest uppercase text-muted-foreground/60">Shipping</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs tabular-nums">
                    {tdProduct.ship_weight && <span><span className="text-muted-foreground">Weight</span> {tdProduct.ship_weight} lb</span>}
                    {tdProduct.length && tdProduct.width && tdProduct.height && (
                      <span><span className="text-muted-foreground">Dims</span> {tdProduct.length}×{tdProduct.width}×{tdProduct.height} in</span>
                    )}
                    {tdProduct.parcel_shippable === 'N' && (
                      <span className="text-amber-500">⚠ heavyweight / special handling</span>
                    )}
                  </div>
                </div>
              )}

              {/* Classification */}
              <SourceField label="Category" value={tdProduct.category_code} mono />

              {/* Description */}
              <SourceField label="Short description" value={tdProduct.short_description} multiline />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// ── QtyOverrideField ─────────────────────────────────────────────────────────

function QtyOverrideField({ c, p, patchMut }: {
  c: CuratedProduct;
  p: TdsynnexProduct;
  patchMut: { mutate: (patch: Parameters<typeof api.curatedUpdate>[1]) => void; isPending: boolean };
}) {
  const isDropShip = (p.qty_on_hand ?? 0) >= 9999 || p.sku_attributes?.[0] === 'Y';
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");

  const tdsynnexQty = isDropShip ? "Unlimited (drop-ship)" : String(p.qty_on_hand ?? 0);
  const hasOverride = c.qty_override != null;
  const effectiveQty = hasOverride ? c.qty_override! : (p.qty_on_hand ?? 0);

  function startEdit() { setVal(hasOverride ? String(c.qty_override) : ""); setEditing(true); }
  function cancel() { setEditing(false); }
  function save() {
    const n = parseInt(val, 10);
    if (!isNaN(n) && n >= 0) {
      patchMut.mutate({ qty_override: n } as any);
    }
    setEditing(false);
  }
  function clear() {
    patchMut.mutate({ qty_override: -1 } as any);
    setEditing(false);
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-medium tracking-widest uppercase text-muted-foreground/60">
          Available inventory
        </p>
        {!editing && (
          <button onClick={startEdit} className="text-[10px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline">
            {hasOverride ? "edit override" : "set override"}
          </button>
        )}
      </div>
      <div className="flex items-center gap-2">
        <p className="text-sm font-medium tabular-nums">{isDropShip ? "∞" : effectiveQty}</p>
        {hasOverride && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-500 border border-amber-500/30">
            manual · TD: {tdsynnexQty}
          </span>
        )}
        {!hasOverride && !isDropShip && (
          <span className="text-[10px] text-muted-foreground/50">TD SYNNEX</span>
        )}
      </div>
      {editing && (
        <div className="flex items-center gap-2 mt-1">
          <input
            type="number"
            min={0}
            value={val}
            onChange={e => setVal(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") cancel(); }}
            placeholder={String(p.qty_on_hand ?? 0)}
            className="w-20 h-7 px-2 text-sm rounded border border-border bg-background tabular-nums focus:outline-none focus:ring-1 focus:ring-ring"
            autoFocus
          />
          <button onClick={save} className="text-xs px-2 py-1 rounded bg-primary text-primary-foreground hover:bg-primary/90">Save</button>
          {hasOverride && <button onClick={clear} className="text-xs text-muted-foreground hover:text-foreground">Clear</button>}
          <button onClick={cancel} className="text-xs text-muted-foreground hover:text-foreground">Cancel</button>
        </div>
      )}
    </div>
  );
}

// ── small components ──────────────────────────────────────────────────────────

function FieldBox({
  label,
  value,
  hint,
  multiline,
  mono,
}: {
  label: string;
  value: string | null | undefined;
  hint?: React.ReactNode;
  multiline?: boolean;
  mono?: boolean;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
        {hint}
      </div>
      <div
        className={[
          "rounded-md border border-border bg-muted/30 px-3 py-2 text-sm",
          multiline ? "whitespace-pre-wrap leading-relaxed" : "truncate",
          mono ? "font-mono text-xs" : "",
        ].join(" ")}
      >
        {value || <span className="text-muted-foreground italic">—</span>}
      </div>
    </div>
  );
}

function SourceField({
  label,
  value,
  multiline,
  mono,
  hint,
}: {
  label: string;
  value: string | null | undefined;
  multiline?: boolean;
  mono?: boolean;
  hint?: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5">{label}</div>
      <div className={`text-sm ${multiline ? "whitespace-pre-wrap" : "truncate"} ${mono ? "font-mono text-xs" : ""}`}>
        {value || <span className="italic text-muted-foreground/60">—</span>}
      </div>
      {hint && <div className="mt-0.5">{hint}</div>}
    </div>
  );
}

function PriceStat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`tabular-nums ${accent ? "text-ember font-semibold text-lg" : "text-base"}`}>
        {value}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case "published": return <Badge variant="success">published</Badge>;
    case "ready":     return <Badge variant="ember">ready</Badge>;
    case "archived":  return <Badge variant="secondary">archived</Badge>;
    default:          return <Badge variant="outline">draft</Badge>;
  }
}

// ── PricingTab ────────────────────────────────────────────────────────────────

interface PricingTabProps {
  pricing: PricingProposal | null;
  pricingLoading: boolean;
  pricingError: boolean;
  curatedId?: string;
  savedPrice: string | null;
  savedCompareAt: string | null;
  savedCompetitors: CompetitorPrice[];
  msrp: number | null;
  tdProduct?: TdsynnexProduct | null;
  onRefresh: () => void;
  onApply: (price: string, compareAt: string | null, competitors: CompetitorPrice[]) => Promise<unknown>;
  applying: boolean;
}

function PricingTab({
  pricing, pricingLoading, pricingError,
  savedPrice, savedCompareAt, savedCompetitors,
  msrp, tdProduct, onRefresh, onApply, applying,
}: PricingTabProps) {
  const [competitors, setCompetitors] = useState<CompetitorPrice[]>(savedCompetitors);
  const [compForm, setCompForm] = useState({ name: "", url: "", price: "" });
  const [scanLog, setScanLog] = useState<string[]>([]);
  const [scanning, setScanning] = useState(false);
  // Controlled price for the slider — strategy presets write here
  const [overridePrice, setOverridePrice] = useState<number | null>(null);

  function addCompetitor() {
    if (!compForm.name || !compForm.price) return;
    setCompetitors(prev => [...prev, { name: compForm.name, url: compForm.url, price: parseFloat(compForm.price) }]);
    setCompForm({ name: "", url: "", price: "" });
  }

  async function saveCompetitors(comps: CompetitorPrice[]) {
    if (!pricing) return;
    await onApply(
      savedPrice ?? String(pricing.proposed),
      savedCompareAt ?? (msrp ? String(msrp) : null),
      comps,
    );
  }

  function runCompetitorScan() {
    if (!tdProduct) return;
    setScanning(true);
    setScanLog(["Starting Claude competitor price scan…"]);
    const params = new URLSearchParams({
      mpn: tdProduct.manufacturer_part_number ?? "",
      brand: tdProduct.brand_slug ?? "",
      upc: tdProduct.upc_code ?? "",
      description: tdProduct.short_description ?? "",
    });
    const es = new EventSource(`/api/competitors/scan/stream?${params}`);
    es.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === "step") setScanLog(l => [...l, msg.text]);
        if (msg.type === "found" && Array.isArray(msg.competitors)) {
          const found: CompetitorPrice[] = msg.competitors.map((c: any) => ({
            name: c.name,
            price: c.price,
            url: c.url ?? "",
          }));
          setCompetitors(found);
          setScanLog(l => [...l, `✓ Found ${found.length} competitor price(s).`]);
        }
        if (msg.type === "done") {
          if (msg.message) setScanLog(l => [...l, msg.message]);
          if (Array.isArray(msg.competitors) && msg.competitors.length > 0) {
            setCompetitors(msg.competitors.map((c: any) => ({
              name: c.name, price: c.price, url: c.url ?? "",
            })));
          }
          setScanning(false);
          es.close();
        }
        if (msg.type === "error") {
          setScanLog(l => [...l, `Error: ${msg.text}`]);
          setScanning(false);
          es.close();
        }
      } catch { /**/ }
    };
    es.onerror = () => { setScanning(false); es.close(); };
  }

  if (pricingLoading) return <div className="h-24 rounded shimmer" />;
  if (pricingError) return <div className="text-destructive text-sm py-4">Pricing service unavailable.</div>;
  if (!pricing) return null;

  const livePrice = overridePrice ?? (parseFloat(savedPrice ?? String(pricing.proposed)) || 0);

  return (
    <div className="space-y-5">
      {/* ── Market Intelligence ── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 text-violet-500" />
              Market intelligence
            </CardTitle>
            <Button
              size="sm"
              variant="outline"
              onClick={runCompetitorScan}
              disabled={scanning || !tdProduct}
              className="text-xs h-7 gap-1.5"
            >
              {scanning
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Sparkles className="h-3.5 w-3.5 text-violet-500" />}
              {scanning ? "Scanning…" : "Analyze with AI"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* SSE log */}
          {scanLog.length > 0 && (
            <div className="rounded-md border border-border bg-muted/30 p-2.5 text-xs font-mono space-y-0.5 max-h-24 overflow-y-auto">
              {scanLog.map((s, i) => (
                <div key={i} className={s.startsWith("✓") ? "text-emerald-400" : s.startsWith("Error") ? "text-rose-400" : "text-muted-foreground"}>{s}</div>
              ))}
              {scanning && <div className="text-muted-foreground animate-pulse">…</div>}
            </div>
          )}

          <PricingIntelligence
            livePrice={livePrice}
            cost={pricing.cost}
            floor={pricing.floor}
            competitors={competitors}
            scanning={scanning}
            onApplyPrice={(price) => {
              setOverridePrice(price);
            }}
          />

          {/* Manual add */}
          <details className="group">
            <summary className="text-xs text-muted-foreground cursor-pointer list-none flex items-center gap-1 hover:text-foreground">
              <Plus className="h-3 w-3" />
              Add competitor manually
            </summary>
            <div className="mt-2 space-y-2">
              <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
                <Input placeholder="Retailer name" value={compForm.name} onChange={e => setCompForm(f => ({ ...f, name: e.target.value }))} className="text-sm h-8" />
                <Input placeholder="Price" value={compForm.price} onChange={e => setCompForm(f => ({ ...f, price: e.target.value }))} className="text-sm h-8 tabular-nums" type="number" step="0.01" />
                <Button size="sm" variant="outline" onClick={addCompetitor} disabled={!compForm.name || !compForm.price} className="h-8">
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
              <Input placeholder="Product URL (optional)" value={compForm.url} onChange={e => setCompForm(f => ({ ...f, url: e.target.value }))} className="text-sm h-8 font-mono" />
            </div>
          </details>

          {competitors.length > 0 && (
            <Button size="sm" variant="ghost" className="w-full text-xs" onClick={() => saveCompetitors(competitors)} disabled={applying}>
              {applying ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1" />}
              Save competitor data
            </Button>
          )}
        </CardContent>
      </Card>

      {/* ── Margin Explorer ── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-emerald-500" />
            Margin explorer
            {overridePrice && (
              <Badge variant="outline" className="text-violet-500 border-violet-500/40 text-[10px] ml-auto">
                Strategy price applied
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <PricingSlider
            proposal={pricing}
            initialPrice={overridePrice ?? undefined}
            onApply={async (price, compareAt) => {
              setOverridePrice(null);
              await onApply(String(price), compareAt ? String(compareAt) : null, competitors);
            }}
          />
        </CardContent>
      </Card>

      {/* ── Cost reference ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <PriceStat label="Cost" value={formatUsd(pricing.cost)} />
        <PriceStat label="MAP floor" value={formatUsd(pricing.floor)} />
        <PriceStat label="Target" value={formatUsd(pricing.target)} />
        <PriceStat label="MSRP ceiling" value={formatUsd(pricing.ceiling)} />
      </div>

      <Button size="sm" variant="ghost" onClick={onRefresh} className="text-xs">
        <RefreshCcw className="h-3.5 w-3.5 mr-1" />
        Recalculate pricing
      </Button>
    </div>
  );
}
